/**
 * <EdgeOnboarding />
 *
 * 7-screen first-visit guided system for THE EDGE. Lands the user on a
 * single behaviour, frames the value, walks them through 4 anchor
 * actions (Identity → Vision → Wealth → Step Into It), then shows the
 * Daily Edge cheat-sheet.
 *
 * Persistence: completion is recorded in localStorage under
 * `reclaim_edge_onboarded_v1`. The /networker landing redirects new
 * users here once.
 *
 * Style: dark amber, full-bleed, single-tap-to-advance, NO clutter.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Crown, Eye, Coins, Sparkles, CheckCircle2,
  Phone, RotateCcw, Moon, X
} from "lucide-react";

const EDGE_ONBOARDED_KEY = "reclaim_edge_onboarded_v1";

export function isEdgeOnboarded() {
  try { return localStorage.getItem(EDGE_ONBOARDED_KEY) === "1"; }
  catch { return false; }
}

export function markEdgeOnboarded() {
  try { localStorage.setItem(EDGE_ONBOARDED_KEY, "1"); } catch (_) {}
}

const SCREENS = [
  {
    key: "entry",
    label: "The Edge",
    title: "THE EDGE",
    sub: "Your daily system for clarity, confidence and performance.",
    body:
      "This takes 5 minutes a day.\nFollow the steps. Stay consistent. Feel the difference.",
    cta: "Set My Edge",
  },
  {
    key: "frame",
    label: "Why this works",
    title: "This is not motivation.",
    sub: "It's a simple system that keeps you in the right state to lead, perform, stay consistent and move forward.",
    body: "Use it daily. That's where the results come from.",
    cta: "Continue",
  },
  {
    key: "identity",
    label: "Step 1",
    icon: Crown,
    title: "Install Your Identity",
    sub: "Record your own affirmations. This becomes your daily anchor.",
    bullets: [
      "I move with confidence",
      "I build a strong team",
      "I create momentum",
      "I handle money with ease",
    ],
    cta: "Record My Identity",
    ctaTo: "/networker/affirmations",
    skipLabel: "I'll do it later",
  },
  {
    key: "vision",
    label: "Step 2",
    icon: Eye,
    title: "Set Your Vision",
    sub: "Create a clear picture of where you're going. You don't need perfection — just feel it.",
    cta: "Open Living Vision",
    ctaTo: "/networker/performance-vision/setup",
    skipLabel: "Skip for now",
  },
  {
    key: "wealth",
    label: "Step 3",
    icon: Coins,
    title: "Activate Wealth",
    sub: "See the numbers. Let them feel normal.",
    cta: "Open Bank of Reclaim",
    ctaTo: "/bank-of-reclaim",
    skipLabel: "Skip for now",
  },
  {
    key: "first_experience",
    label: "Step 4",
    icon: Sparkles,
    title: "Step Into It",
    sub: "Experience your future as already happening.",
    cta: "Start Instant Manifestation",
    ctaTo: "/networker/audio-library?play=edge_instant_manifestation",
    skipLabel: "Skip for now",
  },
  {
    key: "complete",
    label: "You're Set",
    icon: CheckCircle2,
    title: "You're Set",
    sub: "Now keep it simple.",
    daily: [
      { time: "Morning", action: "Run your Identity Activation",  icon: Crown },
      { time: "Before any call", action: "Use Pre-Call Reset",     icon: Phone },
      { time: "After any setback", action: "Use Rejection Reset",  icon: RotateCcw },
      { time: "Anytime",  action: "Use Confidence Reset",          icon: Sparkles },
      { time: "Night",    action: "Use Night Integration",         icon: Moon },
    ],
    cta: "Enter The Edge",
    ctaTo: "/networker",
  },
];

export default function EdgeOnboarding() {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const screen = SCREENS[i];
  const Icon = screen.icon;

  const advance = () => {
    if (i < SCREENS.length - 1) setI(i + 1);
  };
  const finish = (to) => {
    markEdgeOnboarded();
    navigate(to || "/networker");
  };

  const handleCta = () => {
    // Step screens take them to a real anchor; the others just advance.
    if (screen.ctaTo && screen.key !== "complete") {
      // Mark progress and navigate. The user can come back via /networker
      markEdgeOnboarded();
      navigate(screen.ctaTo);
      return;
    }
    if (screen.key === "complete") {
      finish(screen.ctaTo);
      return;
    }
    advance();
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      data-testid="edge-onboarding"
      style={{
        background:
          "radial-gradient(ellipse at 50% 30%, #1A1410 0%, #0E0B08 55%, #050402 100%)",
      }}
    >
      {/* Atmospheric drift */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(circle at 25% 20%, rgba(217,165,90,0.12) 0%, transparent 60%)",
            "radial-gradient(circle at 75% 35%, rgba(217,165,90,0.12) 0%, transparent 60%)",
            "radial-gradient(circle at 25% 20%, rgba(217,165,90,0.12) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Top bar — skip + step counter */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 py-4 z-20">
        <span className="text-[10px] uppercase tracking-[0.4em] text-amber-500/80">
          {screen.label}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">
            {i + 1} / {SCREENS.length}
          </span>
          <button
            onClick={() => finish("/networker")}
            data-testid="edge-onboarding-skip-btn"
            className="text-white/45 hover:text-white/85 transition-colors"
            aria-label="Skip onboarding"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Hairline progress */}
      <div className="absolute top-12 inset-x-0 h-px bg-white/[0.06] z-10">
        <motion.div
          className="h-full"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(217,165,90,0.65), transparent)",
          }}
          animate={{ width: `${((i + 1) / SCREENS.length) * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Screen body */}
      <div className="relative h-full flex flex-col items-center justify-center px-7 pb-32 max-w-xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={screen.key}
            initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
            exit={{    opacity: 0, y: -8, filter: "blur(4px)" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full text-center"
          >
            {Icon && screen.key !== "complete" && (
              <div
                className="mx-auto mb-8 w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(217,165,90,0.30), rgba(217,165,90,0.06))",
                  boxShadow:
                    "0 14px 40px -16px rgba(217,165,90,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
                }}
              >
                <Icon className="w-8 h-8 text-amber-300" strokeWidth={1.5} />
              </div>
            )}
            {Icon && screen.key === "complete" && (
              <motion.div
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "radial-gradient(circle at 50% 40%, rgba(217,165,90,0.55) 0%, rgba(217,165,90,0.10) 60%, transparent 100%)",
                }}
              >
                <Icon className="w-10 h-10 text-amber-200" strokeWidth={1.5} />
              </motion.div>
            )}

            <h1
              className="text-white text-[28px] sm:text-[34px] leading-tight font-semibold mb-4"
              style={{
                letterSpacing: screen.key === "entry" ? "0.18em" : "-0.015em",
                fontFamily: screen.key === "entry"
                  ? "Inter, system-ui, sans-serif"
                  : '"Cormorant Garamond", Georgia, serif',
                fontWeight: screen.key === "entry" ? 600 : 500,
              }}
              data-testid="edge-onboarding-title"
            >
              {screen.title}
            </h1>

            {screen.sub && (
              <p className="text-white/75 text-base sm:text-lg leading-relaxed max-w-md mx-auto">
                {screen.sub}
              </p>
            )}

            {screen.body && (
              <p className="text-white/55 text-sm sm:text-base leading-relaxed mt-5 max-w-md mx-auto whitespace-pre-line">
                {screen.body}
              </p>
            )}

            {screen.bullets && (
              <ul className="mt-7 space-y-2.5 max-w-md mx-auto text-left">
                {screen.bullets.map((b) => (
                  <li
                    key={b}
                    className="px-4 py-3 rounded-xl text-white/85"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(217,165,90,0.16)",
                      fontFamily: '"Cormorant Garamond", Georgia, serif',
                      fontSize: "1.05rem",
                    }}
                  >
                    {b}
                  </li>
                ))}
              </ul>
            )}

            {screen.daily && (
              <div className="mt-7 space-y-2.5 max-w-md mx-auto text-left">
                <p className="text-[10px] uppercase tracking-[0.4em] text-amber-500/70 mb-2">
                  Daily Edge
                </p>
                {screen.daily.map(({ time, action, icon: It }) => (
                  <div
                    key={time}
                    className="px-4 py-3 rounded-xl flex items-center gap-3"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(217,165,90,0.16)",
                    }}
                  >
                    <It className="w-4 h-4 text-amber-300/80 flex-shrink-0" strokeWidth={1.6} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.28em] text-white/35">{time}</p>
                      <p className="text-white/85 text-sm">{action}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom CTA */}
      <div className="absolute bottom-0 inset-x-0 px-7 pb-9 pt-12 z-20"
           style={{ background: "linear-gradient(180deg, transparent, rgba(5,4,2,0.9) 60%, #050402)" }}>
        <div className="max-w-xl mx-auto flex flex-col gap-3">
          <button
            onClick={handleCta}
            data-testid="edge-onboarding-cta-btn"
            className="w-full py-4 rounded-full text-white font-medium text-[15px] tracking-[0.06em] uppercase transition-all duration-300 hover:brightness-110"
            style={{
              background:
                "linear-gradient(135deg, rgba(217,165,90,0.92), rgba(180,120,60,0.95))",
              boxShadow:
                "0 14px 38px -14px rgba(217,165,90,0.65), inset 0 1px 0 rgba(255,255,255,0.10)",
            }}
          >
            <span className="inline-flex items-center gap-2 justify-center">
              {screen.cta}
              <ArrowRight className="w-4 h-4" />
            </span>
          </button>

          {(screen.skipLabel || (i > 0 && i < SCREENS.length - 1)) && (
            <button
              onClick={advance}
              data-testid="edge-onboarding-secondary-btn"
              className="text-white/45 hover:text-white/80 text-xs uppercase tracking-[0.3em] py-2 transition-colors"
            >
              {screen.skipLabel || "Continue"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
